import SurfaceWrapper from './wrappers/SurfaceWrapper/index';
import ThumbgenRenderer from './renderers/ThumbgenRenderer';
import SugarWrapper from './wrappers/SugarWrapper/index';
import MagicSdkWrapper from "./utils/MagicSdkWrapper";
import MlsdkWrapper from "./utils/MlsdkWrapper";
import createLogger from '@sfly/js-logger'
import {
    loadMappingFile,
    canvasToImage,
    extractSizeId,
    cloneObject,
    getAssetKey,
    cacheGet,
    waitFor,
    error,
    warn,
    log,
    RENDERER_TYPE_V0,
    RENDERER_TYPE_THUMBGEN,
    RENDERER_TYPE_V1,
} from "./utils/utils";
import { getLayoutsUrl, getSurfacesUrl, saveToDynamoUrl } from "./utils/globals";

/**
 * @class MagicRenderer
 */
export default class MagicRenderer {

    userId = '';
    env = 'prod';
    size = "small";

    magicSdkWrapper = new MagicSdkWrapper();
    mlsdkWrapper = new MlsdkWrapper();
    jwtToken = "";

    rendered = [];
    renderQueue = [];
    addPhotosQueue = [];
    needLayoutData = [];
    rendering = false;
    magicSDKLoaded = false;
    mlsdkLoaded = false;
    mapping = false;
    thumbGenEnabled = false;
    force = false;
    parallelRendering = false;
    anonymous = false;
    fetchingLayouts = false;
    loadingMagicSDK = false;
    loadingMlsdk = false;
    forceOutputScaling = false;
    logger = null;
    parallelRenderAmount = 2;
    isMagicShop = false;
    isMlsdk = false;

    debug = false;

    /**
     * Constructing a new instance of MagicRenderer
     * @memberOf MagicRenderer
     * @param {MagicRendererInput} params
     * @example
     * const renderer = new MagicRenderer( {env: 'stage',userId: '021060743965', jwtToken: 'eyJraWQiOiJKQUliU0t3cUhZdFdHVDdqMEpGWmlTSHpia090dXExTWdBWHRnNFV2RFF3PSIsImFsZyI6IlJTMjU2In0.eyJzdWIiOiIxZjkzYTE2Yi05YjBjLTRmNGUtOTVhYS1iNjFmNGI0ZTQ0MDIiLCJjb2duaXRvOmdyb3VwcyI6WyJDb2duaXRvTmV3U2lnbnVwIl0sImlzcyI6Imh0dHBzOlwvXC9jb2duaXRvLWlkcC51cy1lYXN0LTEuYW1hem9uYXdzLmNvbVwvdXMtZWFzdC0xX1p4WmxBNVBncCIsImNvZ25pdG86dXNlcm5hbWUiOiIxZjkzYTE2Yi05YjBjLTRmNGUtOTVhYS1iNjFmNGI0ZTQ0MDIiLCJnaXZlbl9uYW1lIjoicGV0ZXIiLCJhdWQiOiIyNzliZ3Q2Z2Fjbmhtbjl1a2d1NjNxcXVoMyIsImV2ZW50X2lkIjoiNTNjM2YzMTUtMjNhZi00NDQ1LWJiNWYtOWY1ZTU2NDQ1ZjcyIiwic2ZseV91aWQiOiIwMjEwNjA3NDM5NjUiLCJ0b2tlbl91c2UiOiJpZCIsInNjb3BlIjoicHJvZmlsZSB1c2VyIiwiYXV0aF90aW1lIjoxNTYxMDM4MjgxLCJleHAiOjE1NjE2NDAyNDQsImlhdCI6MTU2MTYzNjY0NCwiZmFtaWx5X25hbWUiOiJ3b2xmZSIsImVtYWlsIjoic3RhZ2UucGV0ZXJAZ21haWwuY29tIn0.O6DD1zwKaYt17LQg5Ro5ldHiYMGvZ6wbv4gy79chZQvhCvul94yf03f3oavCXdqpnYpehMSsXucEQSPHUMvSjhhfShiHTMyZZhJfnLiHG1CqRWNuqEKhugyXt_OKglNB30yS6GhjEHIegCFyKwcNCabVTnINjpI65bCwjIhoLh6_GrYSl0NXUqRMwa7vgU4egEDUMGOLX9Eo8E5SyURqpZWiN6uE1svvJ2ak5zjYSPhLYLKFrlXH1Yzro8xRUXWXfYUy_BzyRJsM2mkuhO9pNPuV9Zzib8w0oZorzbYjKe4vRzddV08FH2NANhRe5vv89VGCWpP3XIOcOdsniu3HOA', debug: false} );
     */
    constructor(params) {
        // Prevent multiple instances
        if (window.hasOwnProperty("_MagicRendererInstance")) return window._MagicRendererInstance;

        window._MagicRendererInstance = this;

        Object.assign(this, params);

        if (!this.debug) {
            this.debug = window && window.location && window.location.href &&
                window.location.href.indexOf("magicRendererDebug") > -1
        }

        this.createLogger();

        this.debugger(`initiated MagicRenderer`);

        this.isAnonymous();

        this.validate();

        this.loadRenderersMapping();

        if (params.isMagicShop) {
            ThumbgenRenderer.checkAndLoadThumbGen(this.env).catch(() => {
            });
        }
    }

    debugger(str, params) {
        if (this.debug) {
            log(str, params);
        }
    }

    /**
     * Adding photos to the renderer, will pre-fetch the photos rank data for further use
     * @public
     * @memberOf MagicRenderer
     * @param {photo[]} photos
     * @example
     * renderer.addPhotos( [{id:"23030139446"},{id:"23030139411"},{id:"23030140038"},{id:"23030139827"}] );
     */
    addPhotos(photos) {
        if (!Array.isArray(photos) && photos.length > 0) return;

        if (typeof photos[0] === "string") photos = photos.map(id => ({ id }));

        // Prevent duplicates
        const notExisting = photos.filter(({ id }) => !this.magicSdkWrapper.photos.find(photo => photo.id === id));

        if (notExisting.length !== 0) {
            this.debugger("MagicRenderer:: addPhotos called", photos);
            this.magicSdkWrapper.photos.push(...notExisting);

            if (!this.magicSDKLoaded) {
                this.addPhotosQueue.push(...notExisting);
                this.loadMagicSDK();
            } else {
                this.magicSdkWrapper._addPhotos(this.userId, this.jwtToken, notExisting);
            }
        }
    }

    /**
     * Loads Mlsdk, first will check if already on global variable. if not, will download (if needed)
     * @private
     * @memberOf MagicRenderer
     */
    loadMlsdk() {
        this.debugger("MagicRenderer:: loadMlsdk called")
        return new Promise((resolve, reject) => {

            try {
                if (this.loadingMlsdk) {
                    waitFor(() => this.mlsdkLoaded, 2000).then(resolve).catch(reject);
                } else if (!this.mlsdkWrapper.mlsdk) {
                    if (window.mlsdk) {
                        this.mlsdkWrapper.mlsdk = window.mlsdk;
                        this.mlsdkLoaded = true;

                        resolve();
                    } else {
                        this.loadingMlsdk = true;
                        const script = document.createElement("script");
                        script.type = "text/javascript";

                        script.onload = () => {
                            if (window.mlsdk) {
                                this.mlsdkWrapper.mlsdk = window.mlsdk;
                                this.mlsdkLoaded = true;
                                this.loadingMlsdk = false;

                                this.mlsdkWrapper.initMlsdk(this.userId, this.env)

                                resolve();
                            } else {
                                this.loadingMlsdk = false;
                                reject();
                            }
                        };

                        script.onerror = (e) => {
                            this.loadingMlsdk = false;
                            reject(e);
                        };

                        script.src = `${this.mlsdkWrapper?.mlsdkUrl}/mlsdk.min.js`;

                        document.getElementsByTagName('head')[0].appendChild(script);

                    }
                }
            } catch (e) {
                error("MagicRenderer", `failed to load Mlsdk - ${e.message}`);
                reject();
            }

        });
    };

    /**
     * Loads MagicSDK, first will check if passed by the constructor. if not, will download (if needed) and create a new instance
     * @private
     * @memberOf MagicRenderer
     */
    loadMagicSDK() {
        this.debugger("MagicRenderer:: loadMagicSDK called")
        return new Promise((resolve, reject) => {

            try {
                if (this.loadingMagicSDK) {
                    waitFor(() => this.magicSDKLoaded, 2000).then(resolve).catch(reject);
                } else if (!this.magicSdkWrapper.magicSDK) {
                    if (window.MagicSDK) {
                        this.magicSdkWrapper.magicSDK = new window.MagicSDK({ env: this.env, source: "magicshop" });

                        this.magicSDKLoaded = true;
                        if (this.addPhotosQueue.length > 0) this.magicSdkWrapper._addPhotos(this.userId, this.jwtToken, this.addPhotosQueue);
                        if (this.renderQueue.length > 0 && this.mapping !== false && this.rendering === false) this.processRenderQueue();

                        resolve();
                    } else {
                        this.loadingMagicSDK = true;
                        const script = document.createElement("script");
                        script.type = "text/javascript";

                        script.onload = () => {
                            if (window.MagicSDK) {
                                this.magicSdkWrapper.magicSDK = new window.MagicSDK({
                                    env: this.env,
                                    source: "magicshop"
                                });

                                this.magicSDKLoaded = true;
                                if (this.addPhotosQueue.length > 0) this.magicSdkWrapper._addPhotos(this.userId, this.jwtToken, this.addPhotosQueue);
                                if (this.renderQueue.length > 0 && this.mapping && !this.rendering) this.processRenderQueue();
                                this.loadingMagicSDK = false;
                                resolve();
                            } else {
                                this.loadingMagicSDK = false;
                                reject();
                            }
                        };

                        script.onerror = (e) => {
                            this.loadingMagicSDK = false;
                            reject(e);
                        };

                        script.src = this.magicSdkWrapper.magicSDKUrl(this.env);

                        document.getElementsByTagName('head')[0].appendChild(script);

                    }
                }
            } catch (e) {
                error("MagicRenderer", `failed to load MagicSDK - ${e.message}`);
                reject();
            }

        });
    };

    /**
     * Validates the initial input (userId and jwtToken)
     * @private
     * @memberOf MagicRenderer
     */
    validate() {

        if (!this.userId || (!this.anonymous && isNaN(this.userId))) {
            error("Validation Error", "userId is not a number");
        }
        if (!this.jwtToken) {
            error("Validation Error", "jwtToken is not a number");
        }

    };

    /**
     * Generates the thumb of the product, first added to the queue and being rendered when it gets to its turn.
     * @public
     * @memberOf MagicRenderer
     * @param {MagicRendererGenerateInput} params
     * @return HTMLCanvasElement|String
     * @example
     * renderer.generate(
     *     {
            returnAsImage: false,
            classList: [],
            selectedPhotos: [{id: "23030139411"}, {id: "23030140038"}],
            layoutId: 165124,
            sizeId: 617,
            styleId: 22044,
            occasionId: 813,
            layoutName: '617_L_5x7flatcarddigitalfoil_4_cv_22044_elegant-vows_white_S1_a_1',
            addons: {},
            brand: "SFLY",
            callback: (canvas) => {
                console.log(canvas);
            },
            onError: (error) => {
                console.log(error);
            }
        }
     * );
     */
    async generate(params) {
        this.debugger("MagicRenderer:: generate called", params);
        params =
            Object.assign({
                returnAsImage: false,
                classList: [],
                selectedPhotos: [],
                textData: {},
                sizeId: null,
                occasionId: null,
                styleId: null,
                layoutName: null,
                addons: {},
                project: null,
                designJson: null,
                layout: null,
                brand: "SFLY",
                renderer: RENDERER_TYPE_V0,
                callback: null,
                onError: null,
                hasTextData: false,
                generateCPId: false,
                isMlsdk: false,
                skuCode: null,
                formFactor: "",
                productType: "",
                productCode: null,
            }, params);
        try {
            params.hasTextData = params.textData && Object.keys(params.textData).length !== 0;

            // Normalize magicshop params (for handling pre-made projects)
            if (params.project) {
                this.constructor.normalizeMagicShopParams(this.size, params);
            }

            // Make sure we have mapping (parallel stuff)
            if (!this.mapping) {
                await waitFor(() => {
                    return this.mapping;
                }).catch(this.processRenderQueue);
            }

            // Support array of strings (photos)
            this.constructor.normalizePhotostrip(params);

            // Validation and render
            const isValidItem = params.isMlsdk ? this.mlsdkWrapper.validateItem(params) : this.magicSdkWrapper.validateItem(params)
            const hasProject = !!params.project
            if (isValidItem) {
                this.renderQueue.push(params);

                if (!this.rendering && this.mapping !== false && this.rendering === false) {

                    if (!this.mlsdkLoaded && !hasProject && params.isMlsdk) {
                        this.loadMlsdk()
                    } else if (!this.magicSDKLoaded && !hasProject) {
                        this.loadMagicSDK()
                    }

                    this.processRenderQueue();
                }
            }

        } catch (err) {
            if (typeof params.onError === "function") {
                params.onError(err);
            } else {
                throw err;
            }
        }
    };

    /**
     * Immediately generates the thumb of the product, first added to the queue and being rendered when it gets to its turn.
     * @public
     * @memberOf MagicRenderer
     * @param {MagicRendererGenerateInput} params
     * @return HTMLCanvasElement|String
     * @example
     * renderer.immediateGenerate(
     *     {
            returnAsImage: false,
            selectedPhotos: [{id: "23030139411"}, {id: "23030140038"}],
            layoutId: 165124,
            sizeId: 617,
            styleId: 22044,
            occasionId: 813,
            layoutName: '617_L_5x7flatcarddigitalfoil_4_cv_22044_elegant-vows_white_S1_a_1',
            addons: {},
            brand: "SFLY",
            callback: (canvas) => {
                console.log(canvas);
            },
            onError: (error) => {
                console.log(error);
            }
        }
     * );
     */
    async immediateGenerate(params) {
        this.debugger("MagicRenderer:: generate called", params);
        params =
            Object.assign({
                returnAsImage: false,
                classList: [],
                selectedPhotos: [],
                textData: {},
                sizeId: null,
                occasionId: null,
                styleId: null,
                layoutName: null,
                addons: {},
                project: null,
                designJson: null,
                layout: null,
                brand: "",
                renderer: RENDERER_TYPE_V0,
                callback: null,
                onError: null,
                hasTextData: false
            }, params);
        try {

            params.hasTextData = params.hasOwnProperty("textData") && Object.keys(params.textData).length !== 0;

            // Normalize magicshop params (for handling pre-made projects)
            if (params.hasOwnProperty("project") && params.project) {
                this.constructor.normalizeMagicShopParams(this.size, params);
            }

            // Make sure we have mapping (parallel stuff)
            if (this.mapping === false) {
                await waitFor(() => {
                    return this.mapping !== false;
                }).catch((error) => {
                    error("immediateGenerate Error:", error);
                });
            }

            // Fetch layouts for requested items
            this.constructor.setRenderer(params, this.thumbGenEnabled);

            // Support array of strings (photos)
            this.constructor.normalizePhotostrip(params);

            // Get layout data
            await this.getLayoutData(this.env, [params]);

            // Validation and render
            if (this.magicSdkWrapper.validateItem(params)) {

                if (!this.magicSDKLoaded) {
                    await this.loadMagicSDK();
                }

                return this.generateItem(params, true);
            }

        } catch (err) {
            if (typeof params.onError === "function") {
                params.onError(err);
            } else {
                throw err;
            }
        }
    };

    isAnonymous() {
        if (this.userId === "anonymous") {
            this.anonymous = true;
            this.jwtToken = "fakeToken";
        }
    }

    createLogger() {
        this.logger = createLogger('MagicRenderer', { resource: `https://www${this.env !== 'prod' ? `.${this.env}` : ''}.shutterfly.com/application/log.gif` });
    }

    async loadRenderersMapping() {
        this.mapping = await loadMappingFile(this.env);
        if (this.mapping && !this.rendering) this.processRenderQueue();
    }

    static setTextToTextAreas(textAreas, layouts) {
        for (const layout of layouts) {
            if (layout.hasOwnProperty("textWells")) {
                for (const well of layout.textWells) {
                    for (const textArea of textAreas) {
                        if (textArea.id === well.id) {
                            textArea.text = well.text;
                        }
                    }
                }
            }
        }
    }

    static setRenderer(item, thumbGenEnabled = false) {
        if (item.project && item.productType !== "product") {
            item.renderer = RENDERER_TYPE_THUMBGEN;
            return;
        }

        const mapping = cacheGet("mapping");
        if (mapping && typeof mapping === "object" && item.sizeId) {
            if (typeof mapping.sugarIds === "object" && mapping.sugarIds[item.sizeId.toString()]) {
                item.renderer = RENDERER_TYPE_V1;
            } else if (thumbGenEnabled && mapping.magicshop && mapping.magicshop.includes(item.sizeId.toString()) && item.project && item.layout && item.designJson) {
                item.renderer = RENDERER_TYPE_THUMBGEN;
            } else {
                item.renderer = RENDERER_TYPE_V0;
            }

        }

        if (thumbGenEnabled && item.project && item.renderer !== RENDERER_TYPE_V0 && item.renderer !== RENDERER_TYPE_V1) {
            item.renderer = RENDERER_TYPE_THUMBGEN;
        }

        // Fallback to v0 if its Thumbgen and we're missing data
        if (item.renderer === RENDERER_TYPE_THUMBGEN && ((!item.designJson || Object.keys(item.designJson).length === 0 || !item.layout || Object.keys(item.layout).length === 0)) && item.productType === "product") {
            item.renderer = RENDERER_TYPE_V0;
        }

    }

    /**
     * Generates an item when he gets to it turn
     * @private
     * @memberOf MagicRenderer
     */
    async generateItem(item, immediate = false) {
        try {
            this.debugger("Generate Item", item)
            item.timeFromRealGenerateInvoked = Date.now();

            // For mlsdk products extract sizeId from layoutName and set renderer
            if (item.isMlsdk) {
                // If layout name undefined extract default layout name from scheme data
                item.layoutName = item.layoutName ? item.layoutName : this.mlsdkWrapper.extractDefaultLayoutName(item.layoutData, item.formFactor)
                item.sizeId = extractSizeId(item.layoutName)
            }

            this.constructor.setRenderer(item, this.thumbGenEnabled);

            // Thumbgen doesn't need layoutData
            if (item.renderer === RENDERER_TYPE_THUMBGEN) {
                item.layoutData = {};
            }

            try {
                let layoutWithPhotos;
                const MagicRenderer = this;
                if (!item.project) {
                    try {
                        if (item.isMlsdk) {
                            // Check again for mlsdk
                            // Call mlsdk designSurface for generating the wells with the photos
                            if (!this.mlsdkLoaded) await waitFor(() => this.mlsdkLoaded);
                            layoutWithPhotos = await this.mlsdkWrapper.designSurface(item, this.createParamsForSurfaceWrapper, MagicRenderer);
                        } else {
                            // Check again for magic sdk
                            // Call magicsdk fitPhotosToLayout for generating the wells with the photos
                            if (!this.magicSDKLoaded) await waitFor(() => this.magicSDKLoaded);
                            layoutWithPhotos = await this.magicSdkWrapper.fitPhotosToLayout(item, this.userId, this.jwtToken);
                        }
                    } catch (e) {
                        error("MagicRenderer::GenerateItem",
                            `failed to fitPhotosToLayout ${e}`);
                    }
                } else {
                    // Convert project input to fitPhotosToLayout output
                    layoutWithPhotos = this.constructor.fitPhotosToLayoutsOutput(item);
                }

                if (!item.isMlsdk) {
                    // Create params for the SurfaceWrapper
                    await this.createParamsForSurfaceWrapper(item, layoutWithPhotos);
                }
            } catch (e) {
                console.log(e);
                error("MagicRenderer::GenerateItem", `failed to generate surface - ${e.message}`);
            }

        } catch (e) {

            if (item.hasOwnProperty("onError") && typeof item.onError === "function") {
                item.onError(e);
            } else {
                this.debugger(e);
                error("MagicRenderer::GenerateItem", `failed to generate item - ${e.message}`);
            }

            await this.processRenderQueue();
        }
    }

    /**
     * Create params for the SurfaceWrapper
     * @private
     * @memberOf MagicRenderer
     */
    async createParamsForSurfaceWrapper(item, layoutWithPhotos, MagicRenderer = this) {
        try {
            let surfaceWrapperParams;
            if (item.isMlsdk) {
                surfaceWrapperParams = MagicRenderer.mlsdkWrapper.getSurfaceWrapperParams(item, layoutWithPhotos, {
                    env: MagicRenderer.env,
                    size: MagicRenderer.size,
                    isMagicShop: MagicRenderer.isMagicShop
                })
            } else {
                surfaceWrapperParams = MagicRenderer.magicSdkWrapper.getSurfaceWrapperParams(item, layoutWithPhotos, {
                    layoutData: item.layoutData,
                    env: MagicRenderer.env,
                    size: MagicRenderer.size,
                    isMagicShop: MagicRenderer.isMagicShop
                });
            }

            // Append texts from fitPhotosToLayout to the params
            if (layoutWithPhotos?.layouts && surfaceWrapperParams?.layoutData?.textAreas?.length > 0) {
                MagicRenderer.constructor.setTextToTextAreas(surfaceWrapperParams.layoutData.textAreas, layoutWithPhotos.layouts.filter(layout => layout.textWells && layout.textWells.length > 0));
            }

            // Generate surface
            let surface;

            if (item.renderer === "thumbGen" && typeof item.project === "object" && item.designJson && typeof item.layout === "object") {
                surface = "thumbGen";
            } else {
                const surfaceWrapper = new SurfaceWrapper(surfaceWrapperParams);
                surface = await surfaceWrapper.generate();
            }

            // Render item
            if (surface && (surface.constructor.name === "HTMLCanvasElement" || surface === "thumbGen")) {
                item.userId = MagicRenderer.userId;
                const sugarWrapper = new SugarWrapper(MagicRenderer.constructor.getSugarWrapperParams(MagicRenderer.size, item, surface, MagicRenderer.env, MagicRenderer.forceOutputScaling));

                try {
                    const canvas = await sugarWrapper.generate();
                    const result = item.returnAsImage ? canvasToImage(canvas) : canvas;

                    if (item.generateCPId && !item.isMlsdk) {
                        surfaceWrapperParams.addons = item.addons ? item.addons : {};
                        surfaceWrapperParams.brand = item.brand ? item.brand : "SFLY";
                        surfaceWrapperParams.skuCode = item.skuCode ? item.skuCode : "";
                        surfaceWrapperParams.productCode = item.productCode ? item.productCode : "";

                        const product = item.project ? item.project : MagicRenderer.magicSdkWrapper.createProjectJson(layoutWithPhotos, surfaceWrapperParams, MagicRenderer.userId);
                        const resp = await MagicRenderer.saveProjectToDynamo(MagicRenderer.env, { product }, MagicRenderer.anonymous, MagicRenderer.userId, MagicRenderer.jwtToken);
                        if (typeof result.setAttribute !== "undefined" && resp && resp.success) {
                            result.setAttribute("data-id", resp.productId);
                        }
                    }
                    if (typeof result.setAttribute !== "undefined" && Array.isArray(item.classList) && item.classList.length !== 0) {
                        result.setAttribute("class", item.classList.join(" "));
                    }

                    if (typeof result.setAttribute !== "undefined") {
                        result.setAttribute("data-engine", item.renderer);
                    }

                    if (item.isMlsdk) {
                        const project = MagicRenderer.mlsdkWrapper.createProjectJson(item, surfaceWrapperParams);
                        const liteProduct = MagicRenderer.mlsdkWrapper.createLiteProduct(item, layoutWithPhotos);
                        item.callback(result, liteProduct, project);
                    } else {
                        item.callback(result);
                    }

                    // Send metrics
                    item.timeFromRealGenerateInvoked = Date.now() - item.timeFromRealGenerateInvoked;

                    try {
                        if (item.timeFromRealGenerateInvoked > 2000 && item.timeFromRealGenerateInvoked < 60000) {
                            setTimeout(() => {
                                MagicRenderer.logger.metric("MagicRenderer_TimeFromRealGenerateCalled",
                                    item.timeFromRealGenerateInvoked);
                            }, 20);
                        }
                    } catch (e) {
                        // Metrics failed
                    }

                    MagicRenderer.rendered.push(getAssetKey(item, item.isMlsdk));
                    await MagicRenderer.processRenderQueue();

                } catch (e) {
                    console.log(e);
                    error("Render", `failed to render sugar - ${e}`);
                }

            } else {
                error("Render", `failed to render surface - returned value from surface wrapper is not canvas`);
            }
        } catch (e) {
            console.log(e);
            error("MagicRenderer::createParamsForSurfaceWrapper", `failed to generate surface - ${e.message}`);
        }
    }

    /**
     * Iterating the queue and rendering one-by-one
     * @private
     * @memberOf MagicRenderer
     */
    async processRenderQueue() {
        if (this instanceof MagicRenderer) {
            this.debugger("MagicRenderer:: processRenderQueue called");

            const throttledProcessor = setTimeout(async () => {
                if (this.renderQueue.length > 0 && !this.fetchingLayouts) {

                    this.rendering = true;

                    try {
                        await waitFor(() => (this.magicSdkWrapper.photosAdded || this.magicSdkWrapper.photos.length === 0));

                        // Take 2 items every time or if parallel takes all
                        const items = this.renderQueue.splice(0, this.parallelRendering ? this.renderQueue.length : this.parallelRenderAmount);

                        // Get all cached layoutData, The items that has no cache data will be added to needLayoutData array
                        items.forEach(item => this.getCachedLayoutData(item));

                        // Filter mlsdk and magicSdk items
                        const mlsdkItems = []
                        const magicSdkItems = []
                        this.needLayoutData.forEach(item => item.isMlsdk ? mlsdkItems.push(item) : magicSdkItems.push(item));


                        // Get layouts/surfaces for all products (once)
                        if (!this.fetchingLayouts && (this.needLayoutData.length)) {
                            // const getDataPromises = [];

                            // Get Surface data for Mlsdk
                            if (mlsdkItems.length) {
                                // getDataPromises.push(this.getSurfaceData(this.env, mlsdkItems));
                                await this.getSurfaceData(this.env, mlsdkItems);
                            }

                            // Get Layout data for magicSDK
                            if (magicSdkItems.length) {
                                // getDataPromises.push(this.getLayoutData(this.env, magicSdkItems));
                                await this.getLayoutData(this.env, magicSdkItems);
                            }

                            // TODO: Fix promise all problem, if getSurfaceData get stuck or something

                            // Wait for all data from productsService to arrive
                            // await Promise.all(getDataPromises)

                            // Reset needLayoutData for next products
                            this.needLayoutData = [];

                        } else {
                            await waitFor(
                                () => !this.fetchingLayouts || this.needLayoutData.length === 0, 5000,
                                "failed finish with getLayoutData").catch(this.processRenderQueue);
                        }

                        // Render in parallel
                        await Promise.all(items.map(item => this.generateItem(item)));

                        this.processRenderQueue();

                    } catch (e) {
                        this.rendering = false;
                        this.processRenderQueue();
                    }
                } else {
                    this.rendering = false;
                }

                clearTimeout(throttledProcessor);
            }, this.rendered.length !== 0 ? 100 : 0);
        }
    }

    /**
     * Get the layout data from ProductsService using sizeId + occasionId + styleId
     * @private
     * @memberOf MagicRenderer
     */
    async getLayoutData(env, items = []) {
        const products = MagicRenderer.getItemsWithoutData(items)

        if (products.length > 0) {
            try {
                this.fetchingLayouts = true;
                const response = await fetch(getLayoutsUrl(env, this.force), {
                    gzip: true,
                    method: 'POST',
                    body: JSON.stringify(products),
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });

                if (response.status !== 200) {
                    this.fetchingLayouts = false;
                    error("MagicRenderer:: getLayoutData", "failed to fetch layout");
                } else {
                    const resultItems = await response.json();
                    this.magicSdkWrapper.handleGetLayoutsResult(resultItems, items)
                    this.fetchingLayouts = false;
                }
            } catch (e) {
                this.fetchingLayouts = false;
                this.debugger(e);
                error("MagicRenderer:: getLayoutData",
                    `failed to fetch layout data`);
            }
        }
    };


    /**
     * Get the surface data from ProductsService using skuCode + productCode + formFactor
     * @private
     * @memberOf MagicRenderer
     */
    async getSurfaceData(env, items = []) {
        const products = MagicRenderer.getItemsWithoutData(items)

        if (products.length > 0) {
            try {
                this.fetchingLayouts = true;
                const response = await fetch(getSurfacesUrl(env, this.force), {
                    gzip: true,
                    method: 'POST',
                    body: JSON.stringify(products),
                    headers: {
                        'Content-Type': 'application/json'
                    },
                });

                if (response.status !== 200) {
                    this.fetchingLayouts = false;
                    error("MagicRenderer:: getSurfaceData", "failed to fetch surface");
                } else {
                    const resultItems = await response.json();
                    this.mlsdkWrapper.handleGetSurfacesResult(resultItems, items)
                    this.fetchingLayouts = false;
                }
            } catch (e) {
                this.fetchingLayouts = false;
                this.debugger(e);
                warn("MagicRenderer:: getSurfaceData", `failed to fetch surface data: ${e}`)
            }
        }
    };


    /**
     * Extract only needed data for productService api request
     * @param items
     * @returns {[]}
     */
    static getItemsWithoutData(items) {
        const products = [];

        items.forEach(item => {
            const fetchItem = item.isMlsdk ? {
                skuCode: item.skuCode,
                productCode: item.productCode,
                formFactor: item.formFactor
            } : {
                sizeId: item.sizeId,
                occasionId: item.occasionId,
                styleId: item.styleId,
                brand: item.brand
            };
            if (item.layoutName !== null) fetchItem.layoutName = item.layoutName;
            products.push(fetchItem);
        })

        return products;
    }

    /**
     * Generates the SugarRendererV0 params using the item + surface + env
     * @private
     * @memberOf MagicRenderer
     */
    static getSugarWrapperParams(size, item, surface, env, forceOutputScaling = false) {
        const childSkus = Object.keys(item.addons).length > 0 ? this.generateChildSkus(item.layoutData.options, item.addons) : {};
        if (childSkus.sizeId) {
            item.sizeId = childSkus.sizeId;
            delete childSkus.sizeId;
        }
        const sugarWrapperParmas = {
            renderer: item.renderer || RENDERER_TYPE_V0,
            svg: item.layoutData.svg || "",
            env,
            surface,
            size,
            layoutId: item.layoutData.id,
            layoutName: item.layoutName,
            layoutVersion: item.layoutData.assetVersion,
            isLandscape: item.layoutData.isLandscape,
            sizeId: item.sizeId,
            occasionId: item.occasionId,
            styleId: item.styleId,
            brand: item.brand,
            userId: item.userId,
            forceOutputScaling,
            addons: item.addons,
            childSkus,
            defaultFoilColor: item.layoutData.defaultFoilColor
        };

        if (sugarWrapperParmas.renderer === RENDERER_TYPE_THUMBGEN) {
            sugarWrapperParmas.project = item.project;
            sugarWrapperParmas.designJson = item.designJson;
            sugarWrapperParmas.layout = item.layout;
            sugarWrapperParmas.thumbParams = item.thumbParams && Object.keys(item.thumbParams).length !== 0 ? item.thumbParams : {};
        }
        return sugarWrapperParmas;
    };

    static generateChildSkus(options, addons) {
        // Iterate and see if key exist, if yes then append to childskus
        // If the key exist and has options, iterate inside to match others
        const childSkus = {};
        const addonKeys = Object.keys(addons);

        addonKeys.forEach(addonKey => {
            if (addons.addonKey) {
                const addonValue = addons[addonKey];
                if (options[addonKey]?.addonValue) {
                    const match = options[addonKey][addonValue];

                    if (match.priceSku) {
                        childSkus[addonKey] = match.priceSku;
                    }
                    if (match.sizeId) {
                        childSkus["sizeId"] = match.sizeId;
                    }

                    // Match options with all others
                    if (typeof match.options === "object") {

                        addonKeys.forEach(_addonKey => {
                            if (addons.hasOwnProperty(_addonKey)) {
                                const _addonValue = addons[_addonKey];
                                if (match.options[_addonKey] && match.options[_addonKey][_addonValue]) {
                                    const _match = match.options[_addonKey][_addonValue];

                                    if (_match.priceSku) {
                                        childSkus[_addonKey] = _match.priceSku;
                                    }
                                    if (match.sizeId) {
                                        childSkus["sizeId"] = match.sizeId;
                                    }
                                }
                            }
                        });


                    }
                }
            }
        });

        return childSkus;
    }

    /**
     * Normalize magicShop input to MagicRenderer input
     * @param size
     * @param params
     */
    static normalizeMagicShopParams(size, params) {
        params.productType = "product";

        if (params.project.product) params.project = params.project.product;

        if (params.project.info && (!params.project.info.product || params.project.info.product !== "MATISSE_CALENDAR")) {
            params.sizeId = params.project.info.sizeId;
            params.styleId = params.project.info.styleId;
            params.occasionId = params.project.info.occasionId;
            params.brand = "SFLY";
            params.layoutName = params.project.surfaces[0].canvas.layout.layoutId;
            params.addons = params.project.info.addons || {};
        } else if (params.project?.book) {
            params.sizeId = 999;
            params.styleId = params.project.book.info.style;
            params.occasionId = 999;
            params.brand = "SFLY";
            params.layoutName = "";
            params.addons = {};
            params.productType = "photobook";
        } else if (params.project?.prints) {
            params.sizeId = 999;
            params.styleId = 999;
            params.occasionId = 999;
            params.brand = "SFLY";
            params.layoutName = "";
            params.addons = {};
            params.productType = "prints";
        } else if (params.project?.info?.product === "MATISSE_CALENDAR") {
            params.sizeId = 999;
            params.styleId = params.project.info.style;
            params.occasionId = 999;
            params.brand = "SFLY";
            params.layoutName = "";
            params.addons = {};
            params.productType = "calendar";
        }

        if (!params.designJson || !params.layout) {
            params.renderer = RENDERER_TYPE_V0;
        } else {
            if (params.designJson?.default) params.designJson = params.designJson.default;
            params.designJson =
                JSON.parse(JSON.stringify(params.designJson).replace(/\{\{version\}\}/gm, "medium"));

            if (params.productType === "product") {
                Object.assign(params.layout[params.project?.surfaces[0]?.canvas?.layout?.layoutId], params.layout[params.project?.surfaces[0]?.canvas?.layout?.layoutId]?.layout)
            }
        }

    }

    /**
     * Convert magicShop item to magicRenderer item for the continue of the flow
     * @param item
     * @returns {{photoStrip: (*|*[]), layouts: [{textWells: *|[], photos: *|[]}]}}
     */
    static fitPhotosToLayoutsOutput(item) {
        return {
            photoStrip: item.project?.photoStrip || [],
            layouts: [{
                photos: item.project?.surfaces?.length > 0
                    && item.project.surfaces[0]?.canvas?.layout?.photos
                    ? item.project.surfaces[0].canvas.layout.photos.map((photo, key) => {
                        return {
                            photoId: item.project.photoStrip[photo.photoRefId - 1].photoId,
                            wellIndex: photo.seqNum || key,
                            crop: photo.finalCrop
                        };
                    }) : [],
                textWells: item.project?.surfaces
                    ? item.project.surfaces[0].canvas?.layout?.textareas : []
            }]

        }
    }

    static normalizePhotostrip(params) {
        if (Array.isArray(params.selectedPhotos) && params.selectedPhotos.length !== 0 && typeof params.selectedPhotos[0] === "string") {
            params.selectedPhotos = params.selectedPhotos.map(id => ({ id }));
        }
    }

    static hasLayoutData(item) {
        return (item.layoutData !== null && typeof item.layoutData === "object" && Object.keys(item.layoutData).length);
    }

    async saveProjectToDynamo(env, projectJson, anonymous, userId, token) {
        const request = {
            withTTL: true,
            projectJsonStr: JSON.stringify(projectJson)
        };
        if (!anonymous) {
            request.userId = userId;
            request.token = token;
        }
        try {
            const response = await fetch(saveToDynamoUrl(env), {
                method: 'POST',
                body: JSON.stringify(request)
            });

            return await response.json();

        } catch (e) {
            error("generateCPId", `failed to save project in dynamo - ${e}`);
        }
    }

    /**
     * Get the layoutData from cache, and clone it to item.layoutData
     * @param item
     */
    getCachedLayoutData(item) {
        if (item.renderer !== RENDERER_TYPE_THUMBGEN && !MagicRenderer.hasLayoutData(item)) {
            try {
                const layoutData = cacheGet(getAssetKey(item, item.isMlsdk));
                if (layoutData && typeof layoutData === "object" && Object.keys(layoutData).length) {
                    // If there is cacheData clone it to item.layoutData
                    item.layoutData = cloneObject(layoutData);
                } else {
                    // If there is no cache data push item to needLayoutData array
                    this.needLayoutData.push(item)
                }
            } catch (err) {
                if (typeof item.onError === "function") {
                    item.onError(err);
                } else {
                    throw err;
                }
            }
        }
    }
}
